import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { RootState } from '../../store'
import {
  WalletLedgerEntry,
  walletServices,
  WalletSummary,
  WalletTopup,
} from '../../services/wallet-service'
import { kycServices, projectServices } from '../../services'
import { displayDollar, isValidDocType, isValidFileSize } from '../../lib/utils/helpers'
import {
  Button,
  Input,
  InputModal,
  LoaderOverlay,
  RefreshIcon,
  TransactionChecklist,
} from '../../lib/components'
import { showBanner } from '../../global-state/banner-slice'
import { FileUpload, InvestorBankDetails } from '../../lib/types'
import { WalletTopupForm } from './wallet-topup-form'
import { InvestV2InvestorBankCard } from '../invest-v2/invest-v2-investor-bank-card'
import { WithdrawalNoticeModal } from '../withdraw/WithdrawalNoticeModal'
import {
  isPendingLedgerType,
  ledgerReasonForRow,
  pendingTopupReason,
  WALLET_INCOME_LEDGER_LABELS,
  WITHDRAWAL_AWAITING_TRANSFER_REASON,
} from './wallet-ledger'
import { isFeatureEnabled } from '../../lib/utils/feature-flags'
import '../dashboard-v2/dashboard-v2.scss'
import '../invest-v2/invest-v2-bank-cards.scss'
import './wallet.scss'

const LEDGER_LABEL: Record<string, string> = {
  TOPUP_PENDING: 'Deposit',
  TOPUP_CONFIRMED: 'Deposit',
  TOPUP_REJECTED: 'Deposit',
  INVEST_DEBIT: 'Allocation',
  INVEST_RELEASE: 'Redemption',
  REDEEM_CREDIT: 'Redemption',
  ...WALLET_INCOME_LEDGER_LABELS,
  WITHDRAW_PENDING: 'Withdrawal',
  WITHDRAW_SENT: 'Withdrawal',
  WITHDRAW_REJECTED: 'Withdrawal',
  ADMIN_ADJUST: 'Adjustment',
  TRADING_FUND_DEBIT: 'Public Markets',
  TRADING_FUND_REVERSAL: 'Public Markets',
  TRADING_WITHDRAW_CREDIT: 'Public Markets',
}

const LEDGER_STATUS: Record<string, string> = {
  TOPUP_PENDING: 'Pending',
  WITHDRAW_PENDING: 'Pending',
  TOPUP_CONFIRMED: 'Completed',
  WITHDRAW_SENT: 'Completed',
  INVEST_DEBIT: 'Completed',
  INVEST_RELEASE: 'Completed',
  REDEEM_CREDIT: 'Completed',
  MANUAL_SALE_CREDIT: 'Completed',
  INTEREST_CREDIT: 'Completed',
  DIVIDEND_CREDIT: 'Completed',
  ADMIN_ADJUST: 'Completed',
  WITHDRAW_REJECTED: 'Rejected',
  TRADING_FUND_DEBIT: 'Completed',
  TRADING_FUND_REVERSAL: 'Returned',
  TRADING_WITHDRAW_CREDIT: 'Completed',
}

const SUCCESS_TOPUP_SUBTITLE = 'Your top-up request has been raised successfully.'
const SUCCESS_TOPUP_NOTICE = "You'll be notified once the amount is added to your wallet."
const SUCCESS_WITHDRAW_SUBTITLE = 'Your withdrawal request has been raised successfully.'
const SUCCESS_WITHDRAW_NOTICE =
  "Your wallet will be deducted once the transfer to your bank account has been initiated, and you'll be notified at that point."

const money = (cents: number) => displayDollar(String(cents), 'USD', 2, true, true)

/** Exact dollar string from integer cents for amount inputs (453699 → "4536.99"). */
const centsToDollarInput = (cents: number): string => {
  const abs = Math.abs(Math.trunc(cents))
  const whole = Math.floor(abs / 100)
  const frac = String(abs % 100).padStart(2, '0')
  return `${cents < 0 ? '-' : ''}${whole}.${frac}`
}

/** Compact K/M/B for table cells; full decimals under $1,000. */
const compactLedgerAmount = (cents: number): string => {
  const sign = cents < 0 ? '-' : ''
  const absCents = Math.abs(cents)
  const dollars = absCents / 100
  if (dollars < 1000) {
    return `${sign}${money(absCents)}`
  }
  return `${sign}${displayDollar(String(absCents), 'USD', 2, false)}`
}

const creditMoney = (cents: number) => `+${money(Math.abs(cents))}`
const debitMoney = (cents: number) => money(-Math.abs(cents))
const creditCompact = (cents: number) => `+${compactLedgerAmount(Math.abs(cents))}`
const debitCompact = (cents: number) => compactLedgerAmount(-Math.abs(cents))

const isDepositLedgerType = (type: string) =>
  type === 'TOPUP_PENDING' || type === 'TOPUP_CONFIRMED' || type === 'TOPUP_REJECTED'

const isWithdrawalLedgerType = (type: string) =>
  type === 'WITHDRAW_PENDING' || type === 'WITHDRAW_SENT' || type === 'WITHDRAW_REJECTED'

const isOpportunityLedgerType = (type: string) =>
  type === 'INVEST_DEBIT' ||
  type === 'INVEST_RELEASE' ||
  type === 'REDEEM_CREDIT' ||
  type === 'MANUAL_SALE_CREDIT' ||
  type === 'INTEREST_CREDIT' ||
  type === 'DIVIDEND_CREDIT'

const isMoneyIntoWallet = (type: string) =>
  type === 'TOPUP_PENDING' ||
  type === 'TOPUP_CONFIRMED' ||
  type === 'TOPUP_REJECTED' ||
  type === 'REDEEM_CREDIT' ||
  type === 'MANUAL_SALE_CREDIT' ||
  type === 'INTEREST_CREDIT' ||
  type === 'DIVIDEND_CREDIT' ||
  type === 'INVEST_RELEASE' ||
  type === 'TRADING_FUND_REVERSAL' ||
  type === 'TRADING_WITHDRAW_CREDIT'

const isMoneyOutOfWallet = (type: string) =>
  type === 'WITHDRAW_PENDING' ||
  type === 'WITHDRAW_SENT' ||
  type === 'WITHDRAW_REJECTED' ||
  type === 'INVEST_DEBIT' ||
  type === 'TRADING_FUND_DEBIT'

const ledgerStatusForRow = (row: WalletLedgerEntry): string => {
  if (row.type === 'TOPUP_REJECTED' || row.type === 'WITHDRAW_REJECTED') {
    return row.metadata?.cancelledByInvestor === true ? 'Cancelled' : 'Rejected'
  }
  return LEDGER_STATUS[row.type] || 'Completed'
}

const ledgerAmountCentsForDisplay = (row: WalletLedgerEntry): number => {
  if (row.type === 'TOPUP_REJECTED' || row.type === 'WITHDRAW_REJECTED') {
    return Math.abs(row.amount_cents)
  }
  return row.amount_cents
}

const ledgerAmountForRow = (row: WalletLedgerEntry): string => {
  const absCents = Math.abs(ledgerAmountCentsForDisplay(row))
  if (isMoneyOutOfWallet(row.type)) {
    return debitCompact(absCents)
  }
  if (isMoneyIntoWallet(row.type)) {
    return creditCompact(absCents)
  }
  // ADMIN_ADJUST and any other types follow the stored sign.
  const cents = ledgerAmountCentsForDisplay(row)
  if (cents < 0) return debitCompact(cents)
  if (cents > 0) return creditCompact(cents)
  return money(0)
}

const exactLedgerAmountForRow = (row: WalletLedgerEntry): string => {
  const absCents = Math.abs(ledgerAmountCentsForDisplay(row))
  if (isMoneyOutOfWallet(row.type)) {
    return debitMoney(absCents)
  }
  if (isMoneyIntoWallet(row.type)) {
    return creditMoney(absCents)
  }
  const cents = ledgerAmountCentsForDisplay(row)
  if (cents < 0) return debitMoney(cents)
  if (cents > 0) return creditMoney(cents)
  return money(0)
}

const ledgerTransactionLabel = (row: WalletLedgerEntry) => {
  const typeLabel = LEDGER_LABEL[row.type] || row.type
  if (!isOpportunityLedgerType(row.type)) {
    return <span>{typeLabel}</span>
  }
  const name = row.opportunity_name?.trim()
  return (
    <span className='wallet-page__ledger-txn'>
      <span className='wallet-page__ledger-txn-primary'>{name || typeLabel}</span>
      {name ? <span className='wallet-page__ledger-txn-secondary'>{typeLabel}</span> : null}
    </span>
  )
}

const ledgerFeesForRow = (row: WalletLedgerEntry) => ({
  platform_fee_cents: row.fees?.platform_fee_cents ?? 0,
  bank_charges_cents: row.fees?.bank_charges_cents ?? 0,
})

const ledgerExpandPanel = (row: WalletLedgerEntry, reason: string) => {
  const exactAmount = exactLedgerAmountForRow(row)
  const reasonRow = reason ? (
    <div>
      <dt>Reason</dt>
      <dd>{reason}</dd>
    </div>
  ) : null
  if (isDepositLedgerType(row.type)) {
    const fees = ledgerFeesForRow(row)
    return (
      <dl className='wallet-page__ledger-fees'>
        <div>
          <dt>Total amount</dt>
          <dd>{exactAmount}</dd>
        </div>
        <div>
          <dt>Platform fee</dt>
          <dd>{money(fees.platform_fee_cents)}</dd>
        </div>
        <div>
          <dt>Bank charges</dt>
          <dd>{money(fees.bank_charges_cents)}</dd>
        </div>
        {reasonRow}
      </dl>
    )
  }
  if (isWithdrawalLedgerType(row.type)) {
    return (
      <div className='wallet-page__ledger-expand'>
        <dl className='wallet-page__ledger-fees'>
          <div>
            <dt>Total amount</dt>
            <dd>{exactAmount}</dd>
          </div>
          {reasonRow}
        </dl>
        <p className='wallet-page__ledger-disclaimer'>
          The final amount may vary depending on bank charges deducted by the intermediary and
          beneficiary banks, and the net amount will be credited to the account.
        </p>
      </div>
    )
  }
  return (
    <dl className='wallet-page__ledger-fees'>
      <div>
        <dt>Total amount</dt>
        <dd>{exactAmount}</dd>
      </div>
      {reasonRow}
    </dl>
  )
}

const LEDGER_PREVIEW = 3
const LEDGER_EXPANDED = 10

const isSafeInvestReturnPath = (path: string | null): path is string => {
  if (!path) return false
  if (!path.startsWith('/')) return false
  if (path.startsWith('//')) return false
  return path.includes('/invest') || path.includes('/opportunity')
}

const isOpenTopup = (topup: WalletTopup) =>
  topup.status === 'INITIATED' || topup.status === 'ORDER_PLACED'

const formatShortDate = (value: string) =>
  new Date(value).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })

const WalletContainer = () => {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const dispatch = useDispatch()
  const { authToken, userId, investorReference } = useSelector((state: RootState) => state.investor)
  const [wallet, setWallet] = useState<WalletSummary>()
  const [amount, setAmount] = useState('')
  const [busy, setBusy] = useState(false)
  const [bankDetails, setBankDetails] = useState<InvestorBankDetails>()
  const [bankDetailsLoading, setBankDetailsLoading] = useState(false)
  const [openTopups, setOpenTopups] = useState<WalletTopup[]>([])
  const [ledgerExpanded, setLedgerExpanded] = useState(false)
  const [expandedLedgerId, setExpandedLedgerId] = useState<number | null>(null)
  const [walletRefreshing, setWalletRefreshing] = useState(false)
  const [confirmCancelKey, setConfirmCancelKey] = useState<string | null>(null)
  const [successNotice, setSuccessNotice] = useState<{
    subtitle: string
    notice: string
    returnTo?: string
  } | null>(null)
  const [isTransactionChecklistAccepted, setIsTransactionChecklistAccepted] = useState<
    boolean | null
  >(null)

  const mode = searchParams.get('action') || 'overview'
  const modal = mode === 'topup' ? 'add' : mode === 'withdraw' ? 'withdraw' : null
  const amountCentsParam = searchParams.get('amountCents')
  const returnTo = searchParams.get('returnTo')
  const initialAmountDollars =
    amountCentsParam && Number(amountCentsParam) > 0 ? String(Number(amountCentsParam) / 100) : ''

  const openAddModal = () => navigate('/dashboard/wallet?action=topup')
  const openWithdrawModal = () => navigate('/dashboard/wallet?action=withdraw')
  const closeModal = () => {
    setAmount('')
    navigate('/dashboard/wallet')
  }
  const closeSuccessNotice = () => {
    const nextPath = successNotice?.returnTo
    setSuccessNotice(null)
    if (nextPath) {
      navigate(nextPath)
    }
  }

  const loadWallet = useCallback(async () => {
    const res = await walletServices.getWallet({ Authorization: authToken || '' })
    if (res?.status !== 200) return
    setWallet(res.data)

    const fromApi = ((res.data.openTopups || []) as WalletTopup[]).filter(isOpenTopup)
    if (fromApi.length > 0) {
      const detailed = await Promise.all(
        fromApi.map(async (row) => {
          const topupRes = await walletServices.getTopup(row.id, {
            Authorization: authToken || '',
          })
          if (topupRes?.status === 200 && isOpenTopup(topupRes.data)) {
            return topupRes.data as WalletTopup
          }
          return isOpenTopup(row) ? row : null
        }),
      )
      setOpenTopups(detailed.filter((row): row is WalletTopup => Boolean(row)))
      return
    }

    const ledgerRef = (res.data.ledger || []).find(
      (row: WalletLedgerEntry) => row.type === 'TOPUP_PENDING' && row.reference_id,
    )
    const openId = Number(ledgerRef?.reference_id)
    if (!openId) {
      setOpenTopups([])
      return
    }
    const topupRes = await walletServices.getTopup(openId, {
      Authorization: authToken || '',
    })
    if (topupRes?.status === 200 && isOpenTopup(topupRes.data)) {
      setOpenTopups([topupRes.data as WalletTopup])
    } else {
      setOpenTopups([])
    }
  }, [authToken])

  useEffect(() => {
    void loadWallet()
  }, [loadWallet])

  const refreshAvailableBalance = useCallback(async () => {
    if (walletRefreshing) return
    setWalletRefreshing(true)
    try {
      await loadWallet()
    } finally {
      setWalletRefreshing(false)
    }
  }, [loadWallet, walletRefreshing])

  const loadBankDetails = useCallback(async () => {
    if (!userId || !authToken) return
    setBankDetailsLoading(true)
    try {
      const data = await kycServices.getInvestorBankDetails(String(userId), {
        Authorization: authToken,
      })
      if (data && data.data && data.status === 200) {
        setBankDetails(data.data)
      } else {
        setBankDetails(undefined)
      }
    } finally {
      setBankDetailsLoading(false)
    }
  }, [authToken, userId])

  useEffect(() => {
    if (modal === 'withdraw') {
      void loadBankDetails()
    }
  }, [modal, loadBankDetails])

  useEffect(() => {
    if (!authToken || !userId) return
    ;(async () => {
      const response = await kycServices.getKYC_v1(String(userId), {
        Authorization: authToken,
      })
      if (response?.status === 200) {
        setIsTransactionChecklistAccepted(!!response.data.transaction_checklist_agreement)
        return
      }
      setIsTransactionChecklistAccepted(false)
    })()
  }, [authToken, userId])

  const headers = { Authorization: authToken || '' }

  const handleCreateTopup = async (cents: number): Promise<number | undefined> => {
    if (!(cents > 0)) return undefined
    setBusy(true)
    try {
      const created = await walletServices.createTopup({ amount: cents }, headers)
      if (created?.status !== 200) {
        dispatch(
          showBanner({
            text: created?.data?.message || 'Could not create top-up',
            variant: 'error-banner',
          }),
        )
        return undefined
      }
      await loadWallet()
      return created.data?.id as number | undefined
    } finally {
      setBusy(false)
    }
  }

  const handleUpdateAmount = async (topupId: number, cents: number) => {
    if (!(cents > 0)) return
    const topup = openTopups.find((item) => item.id === topupId)
    const currentCents = Math.round(Number(topup?.amount_cents || 0))
    const maxEditCents = Number(wallet?.canIntroduce || 0) + currentCents
    // Keep validation inline under Amount — do not hit the API / top banner.
    if (cents > maxEditCents) return
    setBusy(true)
    try {
      const patched = await walletServices.patchTopup(topupId, { amount: cents }, headers)
      if (patched?.status !== 200) {
        const message = patched?.data?.message || 'Could not update top-up amount'
        // Capacity errors are shown under the amount field in the form.
        if (!/exceeds remaining investment limit/i.test(String(message))) {
          dispatch(
            showBanner({
              text: message,
              variant: 'error-banner',
            }),
          )
        }
        return
      }
      await loadWallet()
    } finally {
      setBusy(false)
    }
  }

  const handleSubmitTopupProof = async (topupId: number, paymentCopy: string) => {
    if (!paymentCopy) return
    setBusy(true)
    try {
      const patched = await walletServices.patchTopup(
        topupId,
        {
          status: 'ORDER_PLACED',
          metadata: {
            completedSteps: 3,
            paymentCopy,
          },
        },
        headers,
      )
      if (patched?.status !== 200) {
        dispatch(
          showBanner({
            text: patched?.data?.message || 'Could not submit top-up proof',
            variant: 'error-banner',
          }),
        )
        return
      }
      await loadWallet()
      closeModal()
      setSuccessNotice({
        subtitle: SUCCESS_TOPUP_SUBTITLE,
        notice: SUCCESS_TOPUP_NOTICE,
        ...(isSafeInvestReturnPath(returnTo) ? { returnTo: decodeURIComponent(returnTo) } : {}),
      })
    } finally {
      setBusy(false)
    }
  }

  const handleCancelTopup = async (topupId: number) => {
    setBusy(true)
    try {
      const patched = await walletServices.patchTopup(topupId, { status: 'CANCELLED' }, headers)
      if (patched?.status !== 200) {
        dispatch(
          showBanner({
            text: patched?.data?.message || 'Could not cancel top-up',
            variant: 'error-banner',
          }),
        )
        return
      }
      dispatch(
        showBanner({
          text: 'Top-up cancelled.',
          variant: 'success-banner',
        }),
      )
      setConfirmCancelKey(null)
      await loadWallet()
    } finally {
      setBusy(false)
    }
  }

  const handleCancelWithdrawal = async (withdrawalId: number) => {
    setBusy(true)
    try {
      const patched = await walletServices.patchWithdrawal(
        withdrawalId,
        { status: 'CANCELLED' },
        headers,
      )
      if (patched?.status !== 200) {
        dispatch(
          showBanner({
            text: patched?.data?.message || 'Could not cancel withdrawal',
            variant: 'error-banner',
          }),
        )
        return
      }
      dispatch(
        showBanner({
          text: 'Withdrawal cancelled.',
          variant: 'success-banner',
        }),
      )
      setConfirmCancelKey(null)
      await loadWallet()
    } finally {
      setBusy(false)
    }
  }

  const handleFileUpload = async (
    fileData: File,
  ): Promise<{ data: FileUpload; status: number } | undefined> => {
    if (isValidDocType(fileData) && isValidFileSize(fileData, 'doc')) {
      const data = await projectServices.postUploadFiles(fileData, {
        Authorization: authToken || '',
      })
      if (data) return data
      return undefined
    }
    dispatch(
      showBanner({
        text: 'File size or type is incorrect for field',
        variant: 'error-banner',
      }),
    )
    return undefined
  }

  const handleWithdraw = async () => {
    const cents = Math.round(Number(amount) * 100)
    const availableCents = wallet?.available || 0
    if (!(cents > 0) || cents > availableCents) return
    setBusy(true)
    try {
      const created = await walletServices.createWithdrawal({ amount: cents }, headers)
      if (created?.status !== 200) {
        dispatch(
          showBanner({
            text: created?.data?.message || 'Could not create withdrawal',
            variant: 'error-banner',
          }),
        )
        return
      }
      await walletServices.patchWithdrawal(
        created.data.id,
        {
          status: 'ORDER_PLACED',
          metadata: { completedSteps: 3, bankDetailsConfirmed: true },
        },
        headers,
      )
      setAmount('')
      await loadWallet()
      closeModal()
      setSuccessNotice({
        subtitle: SUCCESS_WITHDRAW_SUBTITLE,
        notice: SUCCESS_WITHDRAW_NOTICE,
      })
    } finally {
      setBusy(false)
    }
  }

  const availableCents = wallet?.available || 0
  const withdrawCents = Math.round(Number(amount) * 100)
  const withdrawExceedsAvailable = withdrawCents > availableCents

  const withdrawBankDetails: InvestorBankDetails = {
    beneficiaryName: bankDetails?.beneficiaryName || '',
    accountNumber: bankDetails?.accountNumber || '',
    beneficiaryBank: bankDetails?.beneficiaryBank || '',
    bankAddress: bankDetails?.bankAddress || '',
    cif: bankDetails?.cif || '',
    swiftCode: bankDetails?.swiftCode || '',
  }

  const pendingWithdrawRows = useMemo(() => {
    const ledger = wallet?.ledger || []
    return ledger.filter((row) => {
      if (row.type !== 'WITHDRAW_PENDING') return false
      const ref = row.reference_id
      if (ref === null || ref === undefined || ref === '') return true
      const settled = ledger.some(
        (other) =>
          (other.type === 'WITHDRAW_SENT' || other.type === 'WITHDRAW_REJECTED') &&
          String(other.reference_id) === String(ref),
      )
      return !settled
    })
  }, [wallet?.ledger])

  const hasPendingSection = openTopups.length > 0 || pendingWithdrawRows.length > 0

  const sortedLedger = useMemo(() => {
    return [...(wallet?.ledger || [])]
      .filter((row) => !isPendingLedgerType(row.type))
      .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
  }, [wallet?.ledger])

  const visibleLedger = sortedLedger.slice(0, ledgerExpanded ? LEDGER_EXPANDED : LEDGER_PREVIEW)
  const canToggleLedger = sortedLedger.length > LEDGER_PREVIEW

  return (
    <div className='dashboard-v2 wallet-page' data-testid='wallet-page'>
      <div className='dashboard-v2__back-wrap'>
        <button type='button' className='secondary small' onClick={() => navigate('/dashboard')}>
          Back to Dashboard
        </button>
      </div>

      <header className='wallet-page__header'>
        <div>
          <p className='wallet-page__eyebrow'>Wallet</p>
          {investorReference ? <h1>{investorReference}</h1> : <h1>Wallet</h1>}
          <p className='wallet-page__subtitle'>
            Use your available balance to fund your investments directly through the platform.
          </p>
        </div>
      </header>

      <section className='wallet-page__funds-card'>
        <div className='wallet-page__funds-label-row'>
          <p className='wallet-page__funds-label'>Available funds</p>
          <button
            type='button'
            className='wallet-page__funds-refresh'
            aria-label='Refresh available balance'
            disabled={walletRefreshing}
            onClick={() => void refreshAvailableBalance()}
          >
            <RefreshIcon
              width='16'
              height='16'
              classes={[
                'wallet-page__funds-refresh-icon',
                ...(walletRefreshing ? ['wallet-page__funds-refresh-icon--spinning'] : []),
              ]}
            />
          </button>
        </div>
        <h2 className='wallet-page__funds-value'>{money(availableCents)}</h2>
        <p className='wallet-page__funds-hint'>Available to invest or withdraw</p>

        <div className='wallet-page__funds-actions'>
          <button type='button' className='wallet-page__funds-btn' onClick={openAddModal}>
            Add funds
          </button>
          <button type='button' className='wallet-page__funds-btn' onClick={openWithdrawModal}>
            Withdraw funds
          </button>
          {isFeatureEnabled('trading') ? (
            <button
              type='button'
              className='wallet-page__funds-btn'
              onClick={() => navigate('/dashboard/trading')}
            >
              Trade stocks
            </button>
          ) : null}
        </div>
      </section>

      {hasPendingSection ? (
        <section className='wallet-page__pending'>
          <h2>Pending transfer</h2>
          <ul className='wallet-page__pending-list'>
            {openTopups.map((topup) => {
              const cancelKey = `topup-${topup.id}`
              const confirming = confirmCancelKey === cancelKey
              return (
                <li key={cancelKey} className='wallet-page__pending-row'>
                  <div>
                    <p className='wallet-page__pending-title'>
                      Deposit ·{' '}
                      <span className='wallet-page__amount--credit'>
                        {creditMoney(topup.amount_cents)}
                      </span>
                    </p>
                    <p className='wallet-page__pending-meta'>
                      {topup.order_id} · {pendingTopupReason(topup)}
                    </p>
                  </div>
                  <div className='wallet-page__pending-actions'>
                    {confirming ? (
                      <div
                        className='wallet-page__cancel-confirm'
                        data-testid={`wallet-pending-topup-cancel-confirm-${topup.id}`}
                      >
                        <p>Cancel this top-up?</p>
                        <div className='wallet-page__cancel-confirm-actions'>
                          <button
                            type='button'
                            className='secondary small'
                            disabled={busy}
                            data-testid={`wallet-pending-topup-confirm-cancel-${topup.id}`}
                            onClick={() => void handleCancelTopup(topup.id)}
                          >
                            Yes, cancel
                          </button>
                          <button
                            type='button'
                            className='secondary small'
                            disabled={busy}
                            data-testid={`wallet-pending-topup-keep-${topup.id}`}
                            onClick={() => setConfirmCancelKey(null)}
                          >
                            Keep
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className='wallet-page__pending-action-btns'>
                        <button
                          type='button'
                          className='wallet-page__cancel-link'
                          disabled={busy}
                          data-testid={`wallet-pending-topup-continue-${topup.id}`}
                          onClick={openAddModal}
                        >
                          View Details
                        </button>
                        <button
                          type='button'
                          className='wallet-page__cancel-link'
                          disabled={busy}
                          data-testid={`wallet-pending-topup-cancel-${topup.id}`}
                          onClick={() => setConfirmCancelKey(cancelKey)}
                        >
                          Cancel
                        </button>
                      </div>
                    )}
                    <span className='wallet-page__pending-status wallet-page__status--pending'>
                      Pending
                    </span>
                  </div>
                </li>
              )
            })}
            {pendingWithdrawRows.map((row) => {
              const withdrawalId = Number(row.reference_id)
              const cancelKey = `withdraw-${row.id}`
              const confirming = confirmCancelKey === cancelKey
              return (
                <li key={cancelKey} className='wallet-page__pending-row'>
                  <div>
                    <p className='wallet-page__pending-title'>
                      Withdrawal ·{' '}
                      <span className='wallet-page__amount--debit'>
                        {debitMoney(row.amount_cents)}
                      </span>
                    </p>
                    <p className='wallet-page__pending-meta'>
                      Requested {formatShortDate(row.created_at)} ·{' '}
                      {WITHDRAWAL_AWAITING_TRANSFER_REASON}
                    </p>
                  </div>
                  <div className='wallet-page__pending-actions'>
                    {Number.isFinite(withdrawalId) && withdrawalId > 0 ? (
                      confirming ? (
                        <div
                          className='wallet-page__cancel-confirm'
                          data-testid={`wallet-pending-withdraw-cancel-confirm-${withdrawalId}`}
                        >
                          <p>Cancel this withdrawal?</p>
                          <div className='wallet-page__cancel-confirm-actions'>
                            <button
                              type='button'
                              className='secondary small'
                              disabled={busy}
                              data-testid={`wallet-pending-withdraw-confirm-cancel-${withdrawalId}`}
                              onClick={() => void handleCancelWithdrawal(withdrawalId)}
                            >
                              Yes, cancel
                            </button>
                            <button
                              type='button'
                              className='secondary small'
                              disabled={busy}
                              data-testid={`wallet-pending-withdraw-keep-${withdrawalId}`}
                              onClick={() => setConfirmCancelKey(null)}
                            >
                              Keep
                            </button>
                          </div>
                        </div>
                      ) : (
                        <button
                          type='button'
                          className='wallet-page__cancel-link'
                          disabled={busy}
                          data-testid={`wallet-pending-withdraw-cancel-${withdrawalId}`}
                          onClick={() => setConfirmCancelKey(cancelKey)}
                        >
                          Cancel
                        </button>
                      )
                    ) : null}
                    <span className='wallet-page__pending-status wallet-page__status--pending'>
                      Pending
                    </span>
                  </div>
                </li>
              )
            })}
          </ul>
        </section>
      ) : null}

      <section className='wallet-page__ledger'>
        <h2>Transaction ledger</h2>
        {sortedLedger.length === 0 ? (
          <p className='wallet-page__empty'>No wallet activity yet.</p>
        ) : (
          <>
            <div className='wallet-page__ledger-scroll'>
              <table>
                <thead>
                  <tr>
                    <th>Transaction</th>
                    <th>Amount</th>
                    <th>Date</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {visibleLedger.map((row) => {
                    const isExpanded = expandedLedgerId === row.id
                    const status = ledgerStatusForRow(row)
                    const reason = ledgerReasonForRow(row, openTopups)
                    const pending = isPendingLedgerType(row.type)
                    return (
                      <React.Fragment key={row.id}>
                        <tr
                          className={`wallet-page__ledger-row${
                            isExpanded ? ' wallet-page__ledger-row--expanded' : ''
                          }${pending ? ' wallet-page__ledger-row--pending' : ''}`}
                          onClick={() =>
                            setExpandedLedgerId((prev) => (prev === row.id ? null : row.id))
                          }
                          onKeyDown={(event) => {
                            if (event.key === 'Enter' || event.key === ' ') {
                              event.preventDefault()
                              setExpandedLedgerId((prev) => (prev === row.id ? null : row.id))
                            }
                          }}
                          tabIndex={0}
                          aria-expanded={isExpanded}
                          data-testid={`wallet-ledger-row-${row.id}`}
                        >
                          <td>{ledgerTransactionLabel(row)}</td>
                          <td title={exactLedgerAmountForRow(row)}>{ledgerAmountForRow(row)}</td>
                          <td>{formatShortDate(row.created_at)}</td>
                          <td>
                            <span
                              className={
                                pending
                                  ? 'wallet-page__status--pending'
                                  : 'wallet-page__status--completed'
                              }
                            >
                              {status}
                            </span>
                          </td>
                        </tr>
                        {isExpanded ? (
                          <tr
                            className='wallet-page__ledger-fees-row'
                            data-testid={`wallet-ledger-fees-${row.id}`}
                          >
                            <td colSpan={4}>{ledgerExpandPanel(row, reason)}</td>
                          </tr>
                        ) : null}
                      </React.Fragment>
                    )
                  })}
                </tbody>
              </table>
            </div>
            {canToggleLedger ? (
              <button
                type='button'
                className='wallet-page__ledger-toggle'
                onClick={() => setLedgerExpanded((prev) => !prev)}
              >
                {ledgerExpanded ? 'Show less' : 'View more'}
              </button>
            ) : null}
          </>
        )}
      </section>

      {modal === 'add' && isTransactionChecklistAccepted === null ? (
        <InputModal testId='wallet-add-funds-loading-modal' className='wallet-page__modal-backdrop'>
          <LoaderOverlay isVisible message='Loading…' />
        </InputModal>
      ) : null}

      {modal === 'add' && isTransactionChecklistAccepted === false ? (
        <TransactionChecklist
          active
          handleOnClose={() => setIsTransactionChecklistAccepted(true)}
        />
      ) : null}

      {modal === 'add' && isTransactionChecklistAccepted === true ? (
        <InputModal
          testId='wallet-add-funds-modal'
          className='wallet-page__modal-backdrop'
          onBackdropClick={closeModal}
        >
          <div
            className='wallet-page__modal'
            role='dialog'
            aria-labelledby='wallet-add-funds-title'
          >
            <div className='wallet-page__modal-head'>
              <h2 id='wallet-add-funds-title'>Add funds</h2>
              <button
                type='button'
                className='wallet-page__modal-close'
                aria-label='Close'
                onClick={closeModal}
              >
                <span className='wallet-page__modal-close-icon' aria-hidden>
                  ×
                </span>
              </button>
            </div>
            <div className='wallet-page__modal-body'>
              <WalletTopupForm
                openTopups={openTopups}
                canIntroduceCents={wallet?.canIntroduce || 0}
                investmentLimitCents={wallet?.investmentLimit || 0}
                initialAmountDollars={initialAmountDollars}
                isBusy={busy}
                onCreateTopup={handleCreateTopup}
                onUpdateAmount={handleUpdateAmount}
                onSubmitProof={handleSubmitTopupProof}
                onCancelTopup={handleCancelTopup}
                handleFileUpload={handleFileUpload}
                hideTitle
              />
              <LoaderOverlay isVisible={busy} message='Processing…' />
            </div>
          </div>
        </InputModal>
      ) : null}

      {modal === 'withdraw' ? (
        <InputModal
          testId='wallet-withdraw-funds-modal'
          className='wallet-page__modal-backdrop'
          onBackdropClick={closeModal}
        >
          <div
            className='wallet-page__modal'
            role='dialog'
            aria-labelledby='wallet-withdraw-funds-title'
          >
            <div className='wallet-page__modal-head'>
              <h2 id='wallet-withdraw-funds-title'>Withdraw funds</h2>
              <button
                type='button'
                className='wallet-page__modal-close'
                aria-label='Close'
                onClick={closeModal}
              >
                <span className='wallet-page__modal-close-icon' aria-hidden>
                  ×
                </span>
              </button>
            </div>
            <div className='wallet-page__modal-body wallet-page__withdraw'>
              <p>Withdrawal amounts are sent to the bank account provided during your KYC.</p>

              <div className='wallet-page__bank-details-block'>
                <p className='wallet-page__bank-details-heading'>Your bank details</p>
                <InvestV2InvestorBankCard bankDetails={withdrawBankDetails} />
              </div>

              <p className='wallet-page__field-hint'>
                Notice an issue? Contact us at{' '}
                <a href='mailto:support@credibila.com'>support@credibila.com</a>
              </p>

              <div className='wallet-page__amount-field'>
                <div className='wallet-page__amount-label-row'>
                  <div className='input-label'>Amount</div>
                  <button
                    type='button'
                    className='wallet-page__withdraw-all'
                    data-testid='wallet-withdraw-all'
                    disabled={busy || bankDetailsLoading || availableCents <= 0}
                    onClick={() => setAmount(centsToDollarInput(availableCents))}
                  >
                    Withdraw all
                  </button>
                </div>
                <div
                  className='wallet-page__amount-input-wrap'
                  onWheel={(event) => {
                    const target = event.target as HTMLElement | null
                    if (target?.tagName === 'INPUT') {
                      ;(target as HTMLInputElement).blur()
                    }
                  }}
                >
                  <span className='wallet-page__amount-prefix' aria-hidden>
                    $
                  </span>
                  <Input
                    type='number'
                    name='withdraw'
                    placeholder='0.00'
                    value={amount}
                    handleChange={(e) => setAmount(e.target.value)}
                  />
                </div>
              </div>
              {amount.trim() !== '' ? (
                <p
                  className='wallet-page__field-hint wallet-page__field-hint--warning'
                  data-testid='wallet-withdraw-bank-charges-hint'
                >
                  The final amount may vary depending on bank charges deducted by the intermediary
                  and beneficiary banks, and the net amount will be credited to the account.
                </p>
              ) : null}
              {withdrawExceedsAvailable ? (
                <p className='wallet-page__field-hint wallet-page__field-hint--error'>
                  Amount exceeds available balance ({money(availableCents)}).
                </p>
              ) : null}
              <Button
                text='Continue'
                variant='primary-btn'
                isDisabled={
                  busy || bankDetailsLoading || !(withdrawCents > 0) || withdrawExceedsAvailable
                }
                handleClick={() => void handleWithdraw()}
              />
              <LoaderOverlay
                isVisible={busy || bankDetailsLoading}
                message={bankDetailsLoading ? 'Loading bank details…' : 'Processing…'}
              />
            </div>
          </div>
        </InputModal>
      ) : null}

      {successNotice ? (
        <WithdrawalNoticeModal
          variant='success'
          isOpen
          onClose={closeSuccessNotice}
          testId='wallet-success-modal'
          subtitle={successNotice.subtitle}
          notice={successNotice.notice}
        />
      ) : null}
    </div>
  )
}

export { WalletContainer }
